import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const CampaignPerformance = ({ campaigns = [] }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const { isDarkMode } = useTheme();

  const filteredCampaigns = campaigns.filter((campaign) =>
    campaign.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    const statusMap = {
      active: "bg-green-500/10 text-green-500",
      approved: "bg-blue-500/10 text-blue-500",
      paused: "bg-orange-500/10 text-orange-500",
      pending: "bg-orange-500/10 text-orange-500",
      rejected: "bg-red-500/10 text-red-500",
      completed: "bg-purple-500/10 text-purple-500",
    };
    return statusMap[status?.toLowerCase()] || "bg-gray-500/10 text-gray-500";
  };

  const handleCampaignClick = (campaignId) => {
    navigate(`/campaign/${campaignId}`, { state: { from: "homepage" } });
  };

  const containerBg = isDarkMode ? "bg-black" : "bg-white";
  const containerBorder = isDarkMode ? "border-zinc-900" : "border-gray-200";
  const headerText = isDarkMode ? "text-white" : "text-gray-900";
  const tableHeader = isDarkMode ? "text-gray-400" : "text-gray-500";
  const tableRowText = isDarkMode ? "text-white" : "text-gray-800";
  const rowBorder = isDarkMode ? "border-zinc-900" : "border-gray-200";
  const rowHoverBg = isDarkMode ? "hover:bg-zinc-900" : "hover:bg-gray-50";

  const searchBg = isDarkMode ? "bg-black" : "bg-white";
  const searchBorder = isDarkMode ? "border-zinc-800" : "border-gray-300";
  const searchText = isDarkMode ? "text-white" : "text-gray-800";
  const searchPlaceholder = isDarkMode
    ? "placeholder-gray-500"
    : "placeholder-gray-400";
  const searchIcon = isDarkMode ? "text-gray-500" : "text-gray-500";
  const searchFocus = isDarkMode
    ? "focus:border-orange-400"
    : "focus:border-blue-500 focus:ring-1 focus:ring-blue-500";

  return (
    <div
      className={
        isDarkMode ? "bg-black text-white" : "bg-gray-50 text-gray-900"
      }
    >
      <div
        className={`${containerBg} border ${containerBorder} rounded-xl p-5 h-full shadow-lg`}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className={`text-xl font-semibold ${headerText}`}>
            Campaign Performance Table
          </h3>
          <div className="relative">
            <Search
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${searchIcon}`}
              size={16}
            />
            <input
              type="text"
              placeholder="Search by Campaign Name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`${searchBg} border ${searchBorder} rounded-lg pl-9 pr-4 py-2 text-sm ${searchText} ${searchPlaceholder} focus:outline-none ${searchFocus} transition-colors w-64`}
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={`border-b ${rowBorder}`}>
                <th
                  className={`text-left ${tableHeader} text-sm font-medium pb-3`}
                >
                  Campaign Name
                </th>
                <th
                  className={`text-left ${tableHeader} text-sm font-medium pb-3`}
                >
                  Status
                </th>
                <th
                  className={`text-left ${tableHeader} text-sm font-medium pb-3`}
                >
                  Budget
                </th>
                <th
                  className={`text-left ${tableHeader} text-sm font-medium pb-3`}
                >
                  Start Date
                </th>
                <th
                  className={`text-left ${tableHeader} text-sm font-medium pb-3`}
                >
                  End Date
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredCampaigns.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className={`text-center py-10 ${tableHeader}`}
                  >
                    No campaigns found
                  </td>
                </tr>
              ) : (
                filteredCampaigns.map((campaign) => (
                  <tr
                    key={campaign._id}
                    onClick={() => handleCampaignClick(campaign._id)}
                    className={`border-b ${rowBorder} ${rowHoverBg} transition-colors cursor-pointer`}
                  >
                    <td className={`py-3 ${tableRowText}`}>{campaign.title}</td>
                    <td className="py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          campaign.status
                        )}`}
                      >
                        {campaign.status}
                      </span>
                    </td>
                    <td className={`py-3 ${tableRowText}`}>
                      ${campaign.budget?.toLocaleString() || "0"}
                    </td>
                    <td className={`py-3 ${tableRowText}`}>
                      {campaign.startDate
                        ? new Date(campaign.startDate).toLocaleDateString()
                        : "N/A"}
                    </td>
                    <td className={`py-3 ${tableRowText}`}>
                      {campaign.endDate
                        ? new Date(campaign.endDate).toLocaleDateString()
                        : "N/A"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CampaignPerformance;
